import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Eye, Calendar, MapPin, Clock, 
  DollarSign, User, XCircle, AlertTriangle, Check, X, Building2
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Cancel dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Fetch bookings error:', error);
      toast.error('Rezervasyonlar yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(b => 
        b.facility_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.booking_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.payment_status === statusFilter);
    }

    setFilteredBookings(filtered);
  };

  const handleViewDetail = async (booking) => {
    setShowDetailDialog(true);
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/admin/bookings/${booking.booking_id}`, { 
        withCredentials: true 
      });
      setSelectedBooking(response.data);
    } catch (error) {
      console.error('Fetch detail error:', error);
      toast.error('Detay yüklenemedi');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCancelBooking = (booking) => {
    setCancellingBooking(booking);
    setShowCancelDialog(true);
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API}/bookings/${cancellingBooking.booking_id}/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success('Rezervasyon iptal edildi');
      setShowCancelDialog(false);
      fetchBookings();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'İptal başarısız');
    } finally {
      setCancelling(false);
    }
  };

  const handleApproveBankTransfer = async (booking) => {
    if (!window.confirm(`${booking.booking_code} rezervasyonu için havale ödemesini onaylıyor musunuz?`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/bookings/${booking.booking_id}/approve-bank-transfer`,
        {},
        { withCredentials: true }
      );
      toast.success('Havale ödemesi onaylandı! Kullanıcıya SMS gönderildi.');
      fetchBookings();
    } catch (error) {
      console.error('Approve bank transfer error:', error);
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    }
  };

  const handleRejectBankTransfer = async (booking) => {
    if (!window.confirm(`${booking.booking_code} rezervasyonu için havale ödemesini reddediyor musunuz?`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/bookings/${booking.booking_id}/reject-bank-transfer`,
        {},
        { withCredentials: true }
      );
      toast.success('Havale ödemesi reddedildi! Kullanıcıya SMS gönderildi.');
      fetchBookings();
    } catch (error) {
      console.error('Reject bank transfer error:', error);
      toast.error(error.response?.data?.detail || 'Reddetme başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      CONFIRMED: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
      PENDING_PAYMENT: { label: 'Ödeme Bekliyor', color: 'bg-orange-100 text-orange-700' },
      PENDING_BANK_TRANSFER: { label: 'Havale Onay Bekliyor', color: 'bg-blue-100 text-blue-700' },
      CANCELLED: { label: 'İptal Edildi', color: 'bg-red-100 text-red-700' },
      EXPIRED: { label: 'Süresi Doldu', color: 'bg-slate-100 text-slate-700' }
    };
    const c = config[status] || config.PENDING_PAYMENT;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const confirmedCount = bookings.filter(b => b.payment_status === 'CONFIRMED').length;
  const pendingCount = bookings.filter(b => b.payment_status === 'PENDING_PAYMENT' || b.payment_status === 'PENDING_BANK_TRANSFER').length;
  const bankTransferCount = bookings.filter(b => b.payment_status === 'PENDING_BANK_TRANSFER').length;
  const cancelledCount = bookings.filter(b => b.payment_status === 'CANCELLED').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rezervasyonlar</h1>
          <p className="text-sm text-slate-600 mt-1">
            Toplam {bookings.length} • {confirmedCount} Onaylı • {bankTransferCount} Havale Bekliyor • {pendingCount} Bekliyor • {cancelledCount} İptal
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Tesis, kullanıcı veya rezervasyon kodu ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="CONFIRMED">Onaylandı</SelectItem>
                <SelectItem value="PENDING_BANK_TRANSFER">Havale Onay Bekliyor</SelectItem>
                <SelectItem value="PENDING_PAYMENT">Ödeme Bekliyor</SelectItem>
                <SelectItem value="CANCELLED">İptal Edildi</SelectItem>
                <SelectItem value="EXPIRED">Süresi Doldu</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rezervasyon Kodu</TableHead>
                <TableHead>Tesis</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>Tarih & Saat</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Rezervasyon bulunamadı
                  </TableCell>
                </TableRow>
              ) : (
                filteredBookings.map((booking) => (
                  <TableRow key={booking.booking_id}>
                    <TableCell className="font-mono text-sm">
                      {booking.booking_code}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {booking.facility_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3 text-slate-400" />
                        {booking.user_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          {booking.date}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {booking.start_time} - {booking.end_time}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 font-medium">
                        <DollarSign className="w-3 h-3" />
                        {booking.brut_tutar} ₺
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(booking.payment_status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {booking.payment_status === 'PENDING_BANK_TRANSFER' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApproveBankTransfer(booking)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Havale Ödemesini Onayla"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Onayla
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRejectBankTransfer(booking)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Havale Ödemesini Reddet"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reddet
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetail(booking)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detay
                        </Button>
                        {booking.payment_status === 'CONFIRMED' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelBooking(booking)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            İptal
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Rezervasyon Detayları</DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : selectedBooking ? (
            <div className="space-y-4 py-4">
              {/* Booking Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Rezervasyon Kodu</p>
                  <p className="font-mono font-medium">{selectedBooking.booking_code}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Durum</p>
                  <div className="mt-1">{getStatusBadge(selectedBooking.payment_status)}</div>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Tesis</p>
                  <p className="font-medium">{selectedBooking.facility_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Kullanıcı</p>
                  <p className="font-medium">{selectedBooking.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Tarih</p>
                  <p className="font-medium">{selectedBooking.date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Saat</p>
                  <p className="font-medium">{selectedBooking.start_time} - {selectedBooking.end_time}</p>
                </div>
              </div>

              {/* Payment Info */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Ödeme Bilgileri</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Brüt Tutar</p>
                    <p className="font-semibold text-lg">{selectedBooking.brut_tutar} ₺</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Komisyon (%10)</p>
                    <p className="font-semibold text-lg">{selectedBooking.komisyon_tutar || 0} ₺</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">İşletmeci Hakkı</p>
                    <p className="font-semibold text-lg">{selectedBooking.isletmeci_hakki || 0} ₺</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Ödeme Yöntemi</p>
                    <p className="font-medium">PayTR</p>
                  </div>
                </div>
              </div>

              {/* User Info */}
              {selectedBooking.user_info && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3">Kullanıcı Bilgileri</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Email</p>
                      <p className="text-sm">{selectedBooking.user_info.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Telefon</p>
                      <p className="text-sm">{selectedBooking.user_info.phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Facility Info */}
              {selectedBooking.facility_info && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3">Tesis Bilgileri</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Adres</p>
                      <p className="text-sm">{selectedBooking.facility_info.address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Telefon</p>
                      <p className="text-sm">{selectedBooking.facility_info.phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Zaman Bilgileri</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Oluşturulma</p>
                    <p>{selectedBooking.created_at ? format(new Date(selectedBooking.created_at), 'd MMM yyyy HH:mm', { locale: tr }) : '-'}</p>
                  </div>
                  {selectedBooking.hold_expires_at && (
                    <div>
                      <p className="text-slate-600">Hold Bitiş</p>
                      <p>{format(new Date(selectedBooking.hold_expires_at), 'd MMM yyyy HH:mm', { locale: tr })}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Rezervasyon İptali
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-2">
              <strong>{cancellingBooking?.facility_name}</strong> için yapılan rezervasyonu iptal etmek istediğinize emin misiniz?
            </p>
            <div className="bg-slate-50 p-3 rounded-lg text-sm space-y-1">
              <p><strong>Tarih:</strong> {cancellingBooking?.date}</p>
              <p><strong>Saat:</strong> {cancellingBooking?.start_time} - {cancellingBooking?.end_time}</p>
              <p><strong>Tutar:</strong> {cancellingBooking?.brut_tutar} ₺</p>
            </div>
            <p className="text-sm text-orange-600 mt-3">
              ⚠️ İptal işlemi sonrası müşteriye bildirim gönderilecektir.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Vazgeç
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancel}
              disabled={cancelling}
            >
              {cancelling ? 'İptal Ediliyor...' : 'Evet, İptal Et'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsPage;
